import { useEffect, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Switch } from '@cloudflare/kumo/components/switch';
import { Radio } from '@cloudflare/kumo/components/radio';
import { Input } from '@cloudflare/kumo/components/input';
import { Table } from '@cloudflare/kumo/components/table';
import { Badge } from '@cloudflare/kumo/components/badge';
import { FloppyDisk, ArrowCounterClockwise, CloudArrowUp } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import YearSelector, { SupportedYear } from '@/components/YearSelector';

// 年度別の event_settings を編集する画面。
// - 出展者一覧の公開スイッチ (exhibitor_list_visible)
// - 会場マップの公開スイッチ (venue_map_visible)
// - 申込受付モード (registration_override: auto / force_open / force_close)
// - 募集上限 (max_exhibitors)
// - 開催終了 (event_closed): 出展者・スポンサー側からの書き込みを止める
// memory: project_2026_public_listing_switch

type RegistrationOverride = 'auto' | 'force_open' | 'force_close';

interface EventSettings {
  id: string;
  eventYear: number;
  registrationOverride: RegistrationOverride;
  exhibitorListVisible: boolean;
  venueMapVisible: boolean;
  eventClosed: boolean;
  maxExhibitors: number | null;
  currentCount: number;
  registrationStartDate: string | null;
  registrationEndDate: string | null;
  eventStartDate: string;
  eventEndDate: string;
  updatedAt: string;
}

// 受付期間の入力は JST 固定で扱う。datetime-local はタイムゾーンを持たないため、
// 保存時に +09:00 を明示しないと Workers 側 (UTC) で 9 時間ずれる。
function toJstInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

function fromJstInputValue(value: string): string | null {
  if (value.trim() === '') return null;
  return `${value}:00+09:00`;
}

export default function SettingsPage() {
  const [year, setYear] = useState<SupportedYear>(2026);
  const [settings, setSettings] = useState<EventSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // ローカルでトグル中の値 (dirty 状態)
  const [exhibitorListVisible, setExhibitorListVisible] = useState(false);
  const [venueMapVisible, setVenueMapVisible] = useState(false);
  const [eventClosed, setEventClosed] = useState(false);
  const [registrationOverride, setRegistrationOverride] = useState<RegistrationOverride>('auto');
  const [maxExhibitorsDraft, setMaxExhibitorsDraft] = useState<string>('');
  const [startDraft, setStartDraft] = useState<string>('');
  const [endDraft, setEndDraft] = useState<string>('');

  const fetchSettings = async (y: SupportedYear) => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.get<{ data: EventSettings }>(
        `/api/admin/event-settings/${y}`
      );
      const data = res.data.data ?? (res.data as unknown as EventSettings);
      setSettings(data);
      setExhibitorListVisible(data.exhibitorListVisible);
      setVenueMapVisible(data.venueMapVisible);
      setEventClosed(data.eventClosed);
      setRegistrationOverride(data.registrationOverride ?? 'auto');
      setMaxExhibitorsDraft(data.maxExhibitors === null ? '' : String(data.maxExhibitors));
      setStartDraft(toJstInputValue(data.registrationStartDate));
      setEndDraft(toJstInputValue(data.registrationEndDate));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings(year);
  }, [year]);

  const parsedMax = maxExhibitorsDraft.trim() === '' ? null : Number.parseInt(maxExhibitorsDraft, 10);
  const maxInvalid = parsedMax !== null && (!Number.isInteger(parsedMax) || parsedMax < 0);

  const dirty =
    settings !== null &&
    !maxInvalid &&
    (settings.exhibitorListVisible !== exhibitorListVisible ||
      settings.venueMapVisible !== venueMapVisible ||
      settings.eventClosed !== eventClosed ||
      settings.registrationOverride !== registrationOverride ||
      settings.maxExhibitors !== parsedMax ||
      toJstInputValue(settings.registrationStartDate) !== startDraft ||
      toJstInputValue(settings.registrationEndDate) !== endDraft);

  const save = async () => {
    if (!settings) return;
    if (maxInvalid) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.put(`/api/admin/event-settings/${year}`, {
        exhibitorListVisible,
        // 一覧を OFF に戻したらマップも一緒に落とす。マップは出展者名を載せるので、
        // 一覧だけ非公開にしてもマップから漏れては意味がない (API 側も AND で判定)。
        venueMapVisible: exhibitorListVisible && venueMapVisible,
        eventClosed,
        registrationOverride,
        maxExhibitors: parsedMax,
        registrationStartDate: fromJstInputValue(startDraft),
        registrationEndDate: fromJstInputValue(endDraft),
      });
      const data = (res.data?.data ?? res.data) as EventSettings;
      // PUT は currentCount を含まないので保持しておく
      setSettings({ ...data, currentCount: settings.currentCount });
      // 保存された値に画面の状態を揃える。一覧を OFF にしてマップも落ちたのに画面側が
      // ON のまま残ると、一覧を ON に戻した瞬間にマップのスイッチも入って見え、
      // そのまま保存すると意図せずマップを公開してしまう。
      setExhibitorListVisible(data.exhibitorListVisible);
      setVenueMapVisible(data.venueMapVisible);
      setEventClosed(data.eventClosed);
      setInfo('保存しました');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-kumo-default">設定</h1>
        <div className="flex items-center gap-2">
          <YearSelector value={year} onChange={setYear} />
          <Button
            variant="ghost"
            icon={ArrowCounterClockwise}
            loading={loading}
            onClick={() => fetchSettings(year)}
          >
            再読込
          </Button>
        </div>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      {/* 全年度共通 (年度非依存) の会員機能スイッチ。YearSelector とは独立したライフサイクル。 */}
      <MemberFeaturesSection />

      {loading || !settings ? (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          <Surface className="p-6">
            <h2 className="mb-4 text-base font-semibold text-kumo-default">
              {year} 年度 公開設定
            </h2>
            <div className="flex flex-col gap-5">
              <ToggleRow
                label="出展者一覧を公開する"
                description="OFF の間は公式サイトの出展者一覧で「未公開」表示になります。ON にすると paid 状態の出展者がまとめて公開されます。"
                checked={exhibitorListVisible}
                onCheckedChange={setExhibitorListVisible}
              />
              <ToggleRow
                label="会場マップを公開する"
                description="OFF の間は公式サイトの会場マップで「未公開」表示になります。ブース座標のデータが用意できてから ON にしてください。出展者名を載せるため、出展者一覧が非公開の間は ON にできません。"
                checked={exhibitorListVisible && venueMapVisible}
                onCheckedChange={setVenueMapVisible}
                disabled={!exhibitorListVisible}
              />
              <div className="h-px bg-kumo-line" />

              <ToggleRow
                label="開催終了 (出展者・スポンサーからの申込・変更・支払いを停止)"
                description="ON にすると、出展申込・出展内容の編集・隣接配置希望の申請・出展費のお支払い・少額スポンサーのお申し込みを受け付けなくなります。未払いの出展者もマイページからカードで支払えなくなります。公式サイトの少額スポンサー申込ページは受付終了の表示になります。マイページの閲覧とメールアドレス変更・パスキーはそのまま使えます。開催後に出展者から掲載内容の修正を頼まれたときは、一時的に OFF にしてください。"
                checked={eventClosed}
                onCheckedChange={setEventClosed}
              />
              <div className="h-px bg-kumo-line" />

              <div className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm font-medium text-kumo-default">出展申込の受付モード</span>
                  <span className="text-xs text-kumo-muted">
                    現在の申込数:{' '}
                    <span className="font-mono font-semibold text-kumo-default">
                      {settings.currentCount}
                    </span>{' '}
                    /{' '}
                    {settings.maxExhibitors === null ? (
                      <span className="text-kumo-muted">上限なし</span>
                    ) : (
                      <span className="font-mono">{settings.maxExhibitors} 組</span>
                    )}
                  </span>
                </div>
                <Radio.Group
                  legend="受付モード"
                  appearance="card"
                  value={registrationOverride}
                  onValueChange={(v) => setRegistrationOverride(v as RegistrationOverride)}
                >
                  <Radio.Legend className="sr-only">受付モード</Radio.Legend>
                  <Radio.Item
                    label="自動 (期間 + 上限で判定)"
                    description="申込期間内かつ申込数 < 上限のときだけ受付。期間外または上限到達時は自動停止。通常はこれ。"
                    value="auto"
                  />
                  <Radio.Item
                    label="強制受付 (期間・上限を無視して開ける)"
                    description="緊急延長や追加募集など、期間外・上限超過でも受付を強制的に開きたいときに使用。"
                    value="force_open"
                  />
                  <Radio.Item
                    label="強制停止 (期間・上限に関わらず閉じる)"
                    description="緊急停止スイッチ。期間内でも上限未到達でも受付を強制的に閉じたいときに使用。"
                    value="force_close"
                  />
                </Radio.Group>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-kumo-default">募集上限 (組)</span>
                  <span className="text-xs text-kumo-muted">
                    「自動」モードのとき、申込数がこの値以上になると受付が停止します。空欄で「上限なし」運用。
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={maxExhibitorsDraft}
                    onChange={(e) => setMaxExhibitorsDraft(e.currentTarget.value)}
                    className="w-28 text-right font-mono"
                    placeholder="上限なし"
                  />
                  {maxInvalid && (
                    <span className="text-xs text-kumo-danger">0 以上の整数を入力してください</span>
                  )}
                </div>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-kumo-default">受付期間 (JST)</span>
                  <span className="text-xs text-kumo-muted">
                    「自動」モードのときの受付開始/終了時刻。空欄にするとコード側の既定値
                    (2026/7/20 15:00 〜 7/28 23:59) を使います。追加募集のように既定値の期間外へ
                    受付を開けるときに設定してください。
                  </span>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <label className="flex items-center gap-2 text-xs text-kumo-muted">
                    開始
                    <Input
                      type="datetime-local"
                      value={startDraft}
                      onChange={(e) => setStartDraft(e.currentTarget.value)}
                      className="font-mono"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-kumo-muted">
                    終了
                    <Input
                      type="datetime-local"
                      value={endDraft}
                      onChange={(e) => setEndDraft(e.currentTarget.value)}
                      className="font-mono"
                    />
                  </label>
                </div>
              </div>

              <div className="text-xs text-kumo-muted">
                最終更新: {new Date(settings.updatedAt).toLocaleString('ja-JP')}
              </div>
              <div>
                <Button
                  variant="primary"
                  icon={FloppyDisk}
                  disabled={!dirty}
                  loading={saving}
                  onClick={save}
                >
                  保存
                </Button>
              </div>
            </div>
          </Surface>

          {/* [additional-round-2026] 追加募集が終わったらこのセクションごと削除してよい */}
          <RegistrationSlotsSection year={year} />

          <PricingSection year={year} />
        </>
      )}
    </div>
  );
}

// [additional-round-2026] 追加募集の枠。終わったらこのセクション一式を削除してよい。
interface RegistrationSlotRow {
  slotKey: string;
  label: string;
  daysCapability: 'both' | 'saturday' | 'sunday';
  powerCapability: boolean;
  capacity: number;
  sortOrder: number;
  used: number;
  remaining: number;
}

// 追加募集の枠ごとの capacity を編集する。枠の集合 (slot_key) は migration が master で、
// ここでは数だけを動かす。両日×電源ありの枠数が開催前日まで確定しないため、
// デプロイなしで変えられる必要がある。
function RegistrationSlotsSection({ year }: { year: SupportedYear }) {
  const [rows, setRows] = useState<RegistrationSlotRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: { slots?: RegistrationSlotRow[] } }>(
        `/api/admin/event-settings/${year}`
      );
      const list = res.data.data?.slots ?? [];
      setRows(list);
      setDrafts(Object.fromEntries(list.map((r) => [r.slotKey, String(r.capacity)])));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const parsed = rows.map((r) => ({
    row: r,
    value: drafts[r.slotKey]?.trim() === '' ? NaN : Number.parseInt(drafts[r.slotKey] ?? '', 10),
  }));
  const invalid = parsed.some(
    (p) => !Number.isInteger(p.value) || p.value < 0
  );
  const dirty = !invalid && parsed.some((p) => p.value !== p.row.capacity);

  const save = async () => {
    if (invalid || !dirty) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.put(`/api/admin/registration-slots/${year}`, {
        slots: parsed.map((p) => ({ slotKey: p.row.slotKey, capacity: p.value })),
      });
      const data = (res.data?.data ?? res.data) as {
        slots: RegistrationSlotRow[];
        overSubscribed: string[];
      };
      setRows(data.slots ?? []);
      setDrafts(Object.fromEntries((data.slots ?? []).map((r) => [r.slotKey, String(r.capacity)])));
      setInfo(
        data.overSubscribed?.length
          ? `保存しました (${data.overSubscribed.join(', ')} は既に確保済みの申込が上限を超えています。既存の申込は取り消されません)`
          : '保存しました'
      );
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  // 枠が設定されていない年度では、このセクション自体を出さない。
  if (!loading && rows.length === 0) return null;

  return (
    <Surface className="p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-kumo-default">
            {year} 年度 追加募集の枠
          </h2>
          <span className="text-xs text-kumo-muted">
            枠が 1 つでも登録されている間は、上の「募集上限」ではなくこの枠の残数が受付可否を
            決めます。申込内容 (出展日・電源) を収容できる枠のうち、最も専用度が高いものから
            自動的に確保されます。
          </span>
        </div>
        <Button variant="ghost" icon={ArrowCounterClockwise} loading={loading} onClick={fetchRows}>
          再読込
        </Button>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <Loader />
        </div>
      ) : (
        <>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>枠</Table.Head>
                <Table.Head className="text-right">上限 (組)</Table.Head>
                <Table.Head className="text-right">確保済み</Table.Head>
                <Table.Head className="text-right">残り</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => {
                const draft = drafts[row.slotKey] ?? String(row.capacity);
                const value = draft.trim() === '' ? NaN : Number.parseInt(draft, 10);
                const rowInvalid = !Number.isInteger(value) || value < 0;
                return (
                  <Table.Row key={row.slotKey}>
                    <Table.Cell>{row.label}</Table.Cell>
                    <Table.Cell className="text-right">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={draft}
                        onChange={(e) => {
                          // currentTarget はハンドラを抜けると null になるので、
                          // setState の更新関数 (後で実行される) の中で読んではいけない。
                          const value = e.currentTarget.value;
                          setDrafts((prev) => ({ ...prev, [row.slotKey]: value }));
                        }}
                        className="w-24 text-right font-mono"
                      />
                      {rowInvalid && (
                        <div className="text-xs text-kumo-danger">0 以上の整数</div>
                      )}
                    </Table.Cell>
                    <Table.Cell className="text-right font-mono">{row.used}</Table.Cell>
                    <Table.Cell className="text-right">
                      {row.used > row.capacity ? (
                        <Badge variant="warning">上限超過</Badge>
                      ) : row.remaining === 0 ? (
                        <Badge variant="neutral">満席</Badge>
                      ) : (
                        <span className="font-mono">{row.remaining}</span>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          <div className="mt-4">
            <Button
              variant="primary"
              icon={FloppyDisk}
              disabled={!dirty}
              loading={saving}
              onClick={save}
            >
              保存
            </Button>
          </div>
        </>
      )}
    </Surface>
  );
}

interface StripePrice {
  id: string;
  eventYear: number;
  category: string;
  subcategory: string;
  name: string;
  stripePriceId: string;
  amount: number;
  description: string | null;
  isActive: boolean;
}

// 年度別 stripe_prices の表示 / amount 編集 + Stripe 同期スタブ。
// memory: project_2026_stripe_pricing_db_master
function PricingSection({ year }: { year: SupportedYear }) {
  const [rows, setRows] = useState<StripePrice[]>([]);
  const [draftAmounts, setDraftAmounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: StripePrice[] }>(
        `/api/admin/stripe-prices?year=${year}`
      );
      const list = res.data.data ?? [];
      setRows(list);
      setDraftAmounts(Object.fromEntries(list.map((r) => [r.id, r.amount])));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const saveOne = async (row: StripePrice) => {
    const next = draftAmounts[row.id];
    if (next === undefined || next === row.amount) return;
    setSavingId(row.id);
    setError(null);
    try {
      await api.put(`/api/admin/stripe-prices/${row.id}`, {
        name: row.name,
        stripePriceId: row.stripePriceId,
        amount: next,
        description: row.description,
        isActive: row.isActive,
      });
      setInfo(`「${row.name}」を ¥${next.toLocaleString('ja-JP')} に更新しました`);
      await fetchRows();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSavingId(null);
    }
  };

  const sync = async (opts: { force: boolean }) => {
    if (opts.force) {
      if (
        !confirm(
          '既存の Stripe price を archive し、現在の DB amount で新規 price を作成します。\n' +
            '本番アカウントを叩く環境では特に注意してください。続行しますか?'
        )
      ) {
        return;
      }
    }
    setSyncing(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post('/api/admin/stripe-prices/sync', {
        year,
        force: opts.force,
      });
      const data = (res.data?.data ?? res.data) as {
        createdCount: number;
        skippedCount: number;
        errorCount: number;
        errors?: Array<{ id: string; error: string }>;
      };
      setInfo(
        `Stripe 同期完了: 作成 ${data.createdCount} 件 / スキップ ${data.skippedCount} 件 / エラー ${data.errorCount} 件`
      );
      if (data.errors && data.errors.length > 0) {
        setError(data.errors.slice(0, 3).map((e) => e.error).join(' / '));
      }
      await fetchRows();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Surface>
      <div className="flex items-center justify-between border-b border-kumo-border px-4 py-3">
        <div>
          <h2 className="text-base font-semibold text-kumo-default">
            {year} 年度 出展料金 (Stripe Prices)
          </h2>
          <p className="text-xs text-kumo-muted">
            DB を master とした料金マスタ。amount を編集すると新規申込に反映され、Stripe price も
            同じ商品配下で自動的に作り直されます (旧 price は archive)。未同期の行 (price ID 空) は
            「未同期分を Stripe へ同期」で作成してください。決済は同期済みの固定 Price で計上され、
            Stripe コンソールの商品別レポートで数量を集計できます。
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={CloudArrowUp}
            loading={syncing}
            onClick={() => sync({ force: false })}
          >
            未同期分を Stripe へ同期
          </Button>
          <Button
            variant="destructive"
            icon={CloudArrowUp}
            loading={syncing}
            onClick={() => sync({ force: true })}
          >
            強制再作成 (既存 archive)
          </Button>
        </div>
      </div>
      {error && (
        <div className="px-4 pt-3">
          <Banner variant="error">{error}</Banner>
        </div>
      )}
      {info && (
        <div className="px-4 pt-3">
          <Banner variant="default">{info}</Banner>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <Loader />
        </div>
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>区分</Table.Head>
              <Table.Head>明細</Table.Head>
              <Table.Head>名称</Table.Head>
              <Table.Head className="text-right">金額 (円)</Table.Head>
              <Table.Head>Stripe Price ID</Table.Head>
              <Table.Head className="text-right">操作</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => {
              const draft = draftAmounts[row.id] ?? row.amount;
              const dirty = draft !== row.amount;
              return (
                <Table.Row key={row.id}>
                  <Table.Cell>{row.category}</Table.Cell>
                  <Table.Cell>{row.subcategory}</Table.Cell>
                  <Table.Cell>{row.name}</Table.Cell>
                  <Table.Cell className="text-right">
                    <Input
                      type="number"
                      value={String(draft)}
                      onChange={(e) => {
                        // 上と同じ理由で、値は更新関数の外で取り出しておく。
                        const value = Number(e.currentTarget.value) || 0;
                        setDraftAmounts((prev) => ({ ...prev, [row.id]: value }));
                      }}
                      className="w-28 text-right font-mono"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {row.stripePriceId ? (
                      <span className="font-mono text-xs text-kumo-muted">
                        {row.stripePriceId}
                      </span>
                    ) : (
                      <Badge variant="warning">未同期</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    <Button
                      size="sm"
                      variant="primary"
                      icon={FloppyDisk}
                      disabled={!dirty}
                      loading={savingId === row.id}
                      onClick={() => saveOne(row)}
                    >
                      保存
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Surface>
  );
}

// 全年度共通 (年度非依存) の会員機能スイッチ。
// 一般ユーザーのログイン + 会員機能 (出展申込・マイページ等) を一括 ON/OFF する。
// Clerk → Better Auth のユーザー移行が完了するまで OFF にしておく停止ゲート。
// event_settings (年度別) とは別レイヤーなので、YearSelector とは独立に初回のみ fetch する。
function MemberFeaturesSection() {
  const [enabled, setEnabled] = useState(false);
  const [draft, setDraft] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.get<{ data: { memberFeaturesEnabled: boolean } }>(
        '/api/admin/app-settings'
      );
      const data = res.data.data;
      setEnabled(!!data.memberFeaturesEnabled);
      setDraft(!!data.memberFeaturesEnabled);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = draft !== enabled;

  const save = async () => {
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await api.put('/api/admin/app-settings', { memberFeaturesEnabled: draft });
      setEnabled(draft);
      setInfo('保存しました');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface className="p-6">
      <h2 className="mb-1 text-base font-semibold text-kumo-default">
        全年度共通設定 (ログイン)
      </h2>
      <p className="mb-4 text-xs text-kumo-muted">
        この設定は年度に依存しません。現在ログイン中の環境 (staging / production) にのみ作用します。
      </p>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader />
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-5">
          <ToggleRow
            label="一般ユーザーのログインを有効にする"
            description="OFF の間は公式サイトのログイン・出展申込・マイページが「準備中」表示になり、一般ユーザーのログイン経路 (確認コード/リンクのメール送信・パスキー) を停止します。管理者は ADMIN_LOGIN_ALLOWLIST に登録されたメールでのみ引き続きログインできます (パスキーは停止中のため、確認コード/リンクをご利用ください)。Clerk → Better Auth のユーザー移行が完了するまで OFF のままにしておく想定です。"
            checked={draft}
            onCheckedChange={setDraft}
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              icon={FloppyDisk}
              disabled={!dirty}
              loading={saving}
              onClick={save}
            >
              保存
            </Button>
          </div>
        </div>
      )}
    </Surface>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-kumo-default">{label}</span>
        <span className="text-xs text-kumo-muted">{description}</span>
      </div>
      {/* label は span で持っているので、支援技術とテストのために aria-label を明示する。 */}
      <Switch
        aria-label={label}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '保存に失敗しました';
  }
  return e instanceof Error ? e.message : '保存に失敗しました';
}
